import { useEffect, useMemo, useRef, useState } from "react";
import {
  Pill, ChevronDown, Check, X as XIcon, Plus, Sparkles, Layers, AlertTriangle,
  ArrowLeft, ShieldCheck, ShieldX, HelpCircle, Lock, Crown,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { fetchMedications, fetchProducts, runRoutineCheck, getPreferences, savePreferences, saveRoutine, fetchRoutine, fetchPhotoOcrUsage, downloadDiscussionReportPdf } from "../lib/api";
import { useAuth } from "../lib/auth";
import { usePricing } from "../lib/usePricing";
import { useAuthStatus } from "../lib/useAuthStatus";
import { useSearchParams } from "react-router-dom";
import { cn } from "../lib/utils";
import RoutineStepCard from "../components/RoutineStepCard";
import PhotoCapture from "../components/PhotoCapture";
import PhotoOcrLimitModal from "../components/PhotoOcrLimitModal";
import { VerifiedFinding, UncoveredActivesNote, InsufficientActivesPanel } from "../components/VerifiedFindings";
import ReadingProgressBar from "../components/ReadingProgressBar";
import { splitBrandAndName } from "../lib/productDisplay";

const DEFAULT_SLOTS = [
  { slot: "cleanser", label: "Cleanser", hint: "AM + PM cleanser" },
  { slot: "toner", label: "Toner / Essence", hint: "After cleansing" },
  { slot: "serum", label: "Serum", hint: "Targeted treatment" },
  { slot: "moisturizer", label: "Moisturizer", hint: "Hydrate & seal" },
  { slot: "spf", label: "SPF", hint: "AM only" },
];

// Verdict vocabulary — identical enum to the single-check engine so the
// routine result reads the same payload.
const VERDICT_TONE = {
  AVOID: "bg-rose-50 text-rose-700 border-rose-200",
  USE_WITH_CAUTION: "bg-amber-50 text-amber-800 border-amber-200",
  SAFE_TOGETHER: "bg-emerald-50 text-emerald-700 border-emerald-200",
  INSUFFICIENT_DATA: "bg-cream-100 text-ink border-surface-line",
};
const VERDICT_BORDER = {
  AVOID: "border-rose-200",
  USE_WITH_CAUTION: "border-amber-200",
  SAFE_TOGETHER: "border-emerald-200",
  INSUFFICIENT_DATA: "border-surface-line",
};
const VERDICT_LABEL = {
  AVOID: "AVOID",
  USE_WITH_CAUTION: "USE WITH CAUTION",
  SAFE_TOGETHER: "No label conflict found",
  INSUFFICIENT_DATA: "NOT ENOUGH VERIFIED DATA",
};

function VerdictIcon({ verdict, className }) {
  if (verdict === "AVOID") return <ShieldX className={className} aria-label="Avoid" />;
  if (verdict === "USE_WITH_CAUTION") return <AlertTriangle className={className} aria-label="Use with caution" />;
  if (verdict === "SAFE_TOGETHER") return <ShieldCheck className={className} aria-label="No label conflict found" />;
  return <HelpCircle className={className} aria-label="Not enough verified data" />;
}

function routineCountsSummary(counts) {
  if (!counts) return "";
  const parts = [];
  if (counts.AVOID) parts.push(`${counts.AVOID} to avoid`);
  if (counts.USE_WITH_CAUTION) parts.push(`${counts.USE_WITH_CAUTION} with caution`);
  if (counts.SAFE_TOGETHER) parts.push(`${counts.SAFE_TOGETHER} with no conflict`);
  if (counts.INSUFFICIENT_DATA) parts.push(`${counts.INSUFFICIENT_DATA} not yet verified`);
  return parts.join(" · ");
}

export default function Routine({ onAuthNeeded, onPaywall }) {
  const { user } = useAuth();
  const { authEnabled } = useAuthStatus();
  const pricing = usePricing();
  const hasPro = !!user?.is_pro;
  const [medications, setMedications] = useState([]);
  const [medIds, setMedIds] = useState([]);
  const [medOpen, setMedOpen] = useState(false);
  const [medFilter, setMedFilter] = useState("");
  const medRef = useRef(null);

  const [steps, setSteps] = useState(
    DEFAULT_SLOTS.map((s) => ({ ...s, text: "", productName: "", product: null })),
  );
  const [activeStep, setActiveStep] = useState(0);
  const [catalog, setCatalog] = useState([]);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [routineLabel, setRoutineLabel] = useState("");
  const [savingRoutine, setSavingRoutine] = useState(false);
  const [savedRoutineId, setSavedRoutineId] = useState(null);
  const [downloadingReport, setDownloadingReport] = useState(false);

  const handleDownloadReport = async () => {
    if (!result) return;
    setDownloadingReport(true);
    try {
      const blob = await downloadDiscussionReportPdf(result);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "blendsafe-discussion-report.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Discussion report downloaded.");
    } catch (e) {
      if (e?.response?.status === 402) {
        onPaywall?.();
        return;
      }
      toast.error("Couldn't generate the PDF — try again.");
    } finally {
      setDownloadingReport(false);
    }
  };

  // Photo OCR — single dialog at the page root, targeted at one step at a
  // time. The inline counter on each step's "Scan photo" CTA mirrors the
  // session-wide /photo-extract/usage state.
  const [photoUsage, setPhotoUsage] = useState(null);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoPasteOnly, setPhotoPasteOnly] = useState(false);
  const [photoLimit, setPhotoLimit] = useState(null);
  const [photoTargetIdx, setPhotoTargetIdx] = useState(null);
  const refreshPhotoUsage = () => {
    fetchPhotoOcrUsage().then(setPhotoUsage).catch(() => {});
  };
  useEffect(() => { refreshPhotoUsage(); }, []);
  // Captured once on mount: did the page open with ?reopen=? The reopen effect
  // clears that param and `user` can later get a new identity (auth refresh),
  // so re-reading the URL on a later run would wrongly re-enable the prefs
  // hydrate and overwrite the reopened routine's meds. A mount ref is stable.
  const reopeningRef = useRef(
    typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).has("reopen"),
  );
  const openPhotoForStep = (idx) => {
    setPhotoTargetIdx(idx);
    if (photoUsage && photoUsage.remaining === 0) {
      setPhotoLimit({ used: photoUsage.used, limit: photoUsage.limit });
      return;
    }
    setPhotoPasteOnly(false);
    setPhotoOpen(true);
  };

  // Reset save state when a fresh check runs.
  useEffect(() => {
    if (result) setSavedRoutineId(null);
  }, [result]);

  const handleSaveRoutine = async () => {
    if (!user) {
      onAuthNeeded?.();
      return;
    }
    setSavingRoutine(true);
    try {
      const items = (result?.products || [])
        .map((p) => ({
          slot: (p.slot || "").toUpperCase(),
          product_id: p.product?.id || null,
          product_name:
            p.product?.full_name || p.product?.name || p.label || null,
          brand: p.product?.brand || null,
          ingredients_text: p.ingredients_text || null,
        }))
        .filter((i) => i.product_id || i.product_name || i.ingredients_text);
      const selectedMedNames = medications
        .filter((m) => medIds.includes(m.id))
        .map((m) => m.generic_name || m.name);
      const payload = {
        label: routineLabel.trim() || null,
        items,
        medication_ids: medIds,
        medication_names: selectedMedNames,
        verdict: result?.routine_verdict || null,
        score: typeof result?.routine_score === "number" ? result.routine_score : null,
        top_issues: result?.combined_top_issues || [],
      };
      const saved = await saveRoutine(payload);
      setSavedRoutineId(saved.id);
      toast.success("Routine saved", {
        description: "Find it any time on the My routines page.",
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't save this routine.";
      toast.error(typeof msg === "string" ? msg : "Couldn't save this routine.");
    } finally {
      setSavingRoutine(false);
    }
  };

  useEffect(() => {
    fetchMedications().then(setMedications).catch(() => {});
    fetchProducts().then(setCatalog).catch(() => {});
    // Reopening a saved routine → its meds are authoritative; skip the
    // remembered-prefs prefill (mount-captured so a later auth refresh can't
    // re-enable it after ?reopen was cleared and race the reopened meds).
    if (reopeningRef.current) return;
    if (user) {
      getPreferences().then((p) => {
        if (Array.isArray(p.medication_ids) && p.medication_ids.length) {
          setMedIds(p.medication_ids.slice(0, 8));
        }
      }).catch(() => {});
    } else {
      try {
        const saved = JSON.parse(localStorage.getItem("medskin.meds") || "[]");
        if (Array.isArray(saved)) setMedIds(saved.slice(0, 8));
      } catch (_e) { /* ignore */ }
    }
  }, [user]);

  // Reopen flow — `/routine?reopen=:id` populates meds + steps from a saved
  // routine so the user can re-check it without rebuilding.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const reopenId = searchParams.get("reopen");
    if (!reopenId || !user) return;
    let cancelled = false;
    fetchRoutine(reopenId)
      .then((r) => {
        if (cancelled) return;
        if (Array.isArray(r.medication_ids)) {
          setMedIds(r.medication_ids.slice(0, 8));
        }
        // Map saved items back into the slot-keyed steps array.
        const map = new Map(
          (r.items || []).map((it) => [(it.slot || "").toLowerCase(), it]),
        );
        setSteps((current) =>
          current.map((s) => {
            const saved = map.get(s.slot);
            if (!saved) return s;
            return {
              ...s,
              text: saved.ingredients_text || "",
              productName: saved.product_name || "",
              product: saved.product_id
                ? {
                    id: saved.product_id,
                    name: saved.product_name,
                    full_name: saved.product_name,
                    brand: saved.brand,
                  }
                : null,
            };
          }),
        );
        if (r.label) setRoutineLabel(r.label);
        toast.message("Routine reopened", {
          description: "Tap 'Run routine check' to re-evaluate.",
        });
        // Clear the query param so a refresh doesn't re-trigger.
        const next = new URLSearchParams(searchParams);
        next.delete("reopen");
        setSearchParams(next, { replace: true });
      })
      .catch(() => {
        toast.error("Couldn't reopen that routine", {
          description: "It may have been deleted, or your link has expired. Build a new one any time.",
        });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Prefill medication from `/routine?med=:id` (homepage hero chips and
  // quick-select boxes land here).
  useEffect(() => {
    const medId = searchParams.get("med");
    if (!medId || medications.length === 0) return;
    if (medications.some((m) => m.id === medId)) {
      setMedIds((prev) => (prev.includes(medId) ? prev : [...prev, medId].slice(0, 8)));
    }
    const next = new URLSearchParams(searchParams);
    next.delete("med");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medications]);

  useEffect(() => {
    const handler = (e) => {
      if (medRef.current && !medRef.current.contains(e.target)) {
        setMedOpen(false);
      }
    };
    if (medOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [medOpen]);

  const selectedMeds = useMemo(
    () => medIds.map((id) => medications.find((m) => m.id === id)).filter(Boolean),
    [medIds, medications],
  );
  const toggleMed = (id) =>
    setMedIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const filteredMeds = useMemo(() => {
    const q = medFilter.trim().toLowerCase();
    return q
      ? medications.filter(
          (m) =>
            (m.name || "").toLowerCase().includes(q) ||
            (m.generic_name || "").toLowerCase().includes(q),
        )
      : medications;
  }, [medFilter, medications]);

  const setStep = (i, field, val) =>
    setSteps((p) => p.map((s, idx) => (idx === i ? { ...s, [field]: val } : s)));

  // Per user request: no automatic scrolling during the routine flow.
  // All navigation through the step pipeline is now driven exclusively
  // by user gestures (taps, Next/Skip buttons) and never moves the
  // viewport.

  const goToStep = (idx) => {
    if (idx < 0 || idx >= steps.length) return;
    setActiveStep(idx);
  };

  const advance = () => {
    if (activeStep < steps.length - 1) {
      goToStep(activeStep + 1);
    } else {
      submit();
    }
  };

  const goBack = () => goToStep(Math.max(0, activeStep - 1));

  const submit = async () => {
    // Result rendering must NEVER depend on auth/pro status. We do not block
    // submit with a proactive paywall — even quota-exhausted users get the
    // best inline outcome we can show, and any premium upsell is rendered
    // BELOW the verdict (or as an inline hint), never as an overlay.
    if (!medIds.length) return toast.warning("Pick a medication first", {
      description: "Add at least one medication so we can evaluate every step in your routine.",
    });
    const routine = steps
      .filter((s) => (s.product && s.product.id) || s.text.trim().length > 0)
      .map((s) => {
        const p = s.product;
        return {
          slot: s.slot,
          label: s.label,
          product_input: p
            ? { product_id: p.id, product_name: p.full_name || p.name }
            : {
                ingredient_text: s.text,
                product_name: s.productName.trim() || s.label,
              },
        };
      });
    if (!routine.length) return toast.warning("Add at least one product", {
      description: "Drop in a product or paste an ingredient list for at least one step.",
    });

    setRunning(true);
    setResult(null);
    try {
      const data = await runRoutineCheck({
        medication_ids: medIds,
        routine,
        // A reopened saved routine is evaluated WITHOUT rewriting the user's
        // remembered meds / tracker candidates (server skips the auto-save).
        remember: !reopeningRef.current,
      });
      setResult(data);
      if (user && !user.is_pro) user.checks_used = (user.checks_used || 0) + 1;
      // Remember the working set (for re-check alerts and the next visit) —
      // but NEVER when re-checking a REOPENED saved routine. Doing so would
      // silently overwrite the user's profile medications and Account
      // treatment-tracker candidates. A reopened routine is persisted only via
      // the explicit "Save routine" action.
      if (user && !reopeningRef.current) {
        savePreferences({
          medication_ids: medIds,
          routine: routine,
        }).catch(() => {});
      }
    } catch (e) {
      // 402 = quota exhausted. Render an inline "limit reached" result
      // panel instead of opening the global Pro modal — overlay would
      // hide the routine the user just finished entering.
      if (e?.response?.status === 402) {
        setResult({
          quota_exhausted: true,
          message:
            e?.response?.data?.detail?.message ||
            "You've used your free routine checks for this month.",
          upgrade_url: e?.response?.data?.detail?.upgrade_url || "/account?upgrade=true",
        });
        return;
      }
      const detailMsg = e?.response?.data?.detail;
      toast.error("Routine check didn't finish", {
        description: typeof detailMsg === "string" ? detailMsg : "Check your connection and try again \u2014 your routine draft is still here.",
      });
    } finally {
      setRunning(false);
    }
  };

  if (!hasPro) {
    return (
      <main
        className="min-h-screen bg-cream-50 pt-32 sm:pt-36 pb-24 px-4"
        data-testid="routine-page"
      >
        <div className="max-w-xl mx-auto" data-testid="routine-pro-gate">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-peach-700 mb-3">
            <Layers className="w-3.5 h-3.5" />
            Routine Checker
          </div>
          <div className="rounded-3xl border border-surface-line bg-white p-7 sm:p-9 shadow-subtle">
            <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-cream-100 border border-surface-line text-ink">
              <Crown className="w-5 h-5 text-peach-500" />
            </span>
            <h1 className="mt-5 text-2xl sm:text-3xl font-extrabold tracking-[-0.02em] text-ink">
              Check your whole routine with Pro
            </h1>
            <p className="mt-3 text-ink-soft text-[15px] leading-relaxed">
              The Routine Checker evaluates up to 5 products at once against your
              medications and gives you one combined verdict. It's part of
              BlendSafe Pro — single-product checks stay free, always.
            </p>
            <ul className="mt-5 space-y-2.5">
              {[
                "Check up to 5 products in one pass",
                "Combined routine verdict + per-step flags",
                "Save routines and re-check after a med change",
                "Dermatologist discussion report (PDF)",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-[14px] text-ink-soft">
                  <Check className="w-4 h-4 text-safe-deep mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <div className="mt-7 flex flex-col sm:flex-row gap-2.5">
              {!user ? (
                <>
                  <button
                    type="button"
                    data-testid="routine-gate-signin"
                    onClick={() => onAuthNeeded?.()}
                    className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-2xl bg-ink hover:bg-ink-soft text-cream-50 text-[14px] font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
                  >
                    Sign in to continue
                  </button>
                  <button
                    type="button"
                    data-testid="routine-gate-upgrade"
                    onClick={() => onPaywall?.()}
                    className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-2xl border border-surface-line bg-cream-50 text-ink text-[14px] font-semibold transition-all hover:bg-cream-100"
                  >
                    <Lock className="w-4 h-4" />
                    See Pro · {pricing.formatted}/{pricing.interval}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  data-testid="routine-gate-upgrade"
                  onClick={() => onPaywall?.()}
                  className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-2xl bg-ink hover:bg-ink-soft text-cream-50 text-[14px] font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
                >
                  <Sparkles className="w-4 h-4" />
                  Upgrade to Pro · {pricing.formatted}/{pricing.interval}
                </button>
              )}
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-cream-50 pt-32 sm:pt-36 pb-24 px-4" data-testid="routine-page">
      <ReadingProgressBar />
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-peach-700 mb-3">
          <Layers className="w-3.5 h-3.5" />
          Routine Checker
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-ink">
          Check your whole routine
        </h1>
        <p className="mt-2 text-ink-mute text-base max-w-xl">
          Add your skincare steps and the medications you take. We'll check each
          product and give you one clear verdict for the whole routine.
        </p>

        {/* Medication multi-select */}
        <div ref={medRef} className="relative w-full mt-6">
          {/*
            div+role=button (not <button>) so the inner per-chip "remove" buttons
            don't violate the HTML rule that a <button> cannot contain another
            <button>. Keyboard semantics preserved via tabIndex + Enter/Space.
          */}
          <div
            role="button"
            tabIndex={0}
            data-testid="routine-medication-trigger"
            onClick={() => setMedOpen((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setMedOpen((v) => !v);
              }
            }}
            aria-expanded={medOpen}
            className="w-full min-h-16 px-4 py-3 rounded-2xl bg-white border border-surface-line flex items-start gap-3 text-left hover:bg-cream-100 focus:outline-none focus:ring-4 focus:ring-peach-100 cursor-pointer"
          >
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-cream-100 border border-surface-line text-ink-soft mt-0.5">
              <Pill className="w-4 h-4" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-[10px] uppercase tracking-[0.18em] text-ink-light font-semibold">
                Medications {selectedMeds.length > 0 && (
                  <span className="text-peach-500">· {selectedMeds.length}</span>
                )}
              </span>
              {selectedMeds.length > 0 ? (
                <span className="mt-1.5 flex flex-wrap gap-1.5">
                  {selectedMeds.map((m) => {
                    const label = m.name || m.generic_name || m.id?.slice(0, 6);
                    return (
                      <span key={m.id}
                        data-testid={`routine-med-chip-${m.id}`}
                        className="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 rounded-full bg-cream-100 border border-surface-line text-[12px] font-medium text-ink">
                        {label}
                        <button type="button" onClick={(e) => { e.stopPropagation(); toggleMed(m.id); }}
                          className="ml-0.5 inline-flex w-4 h-4 items-center justify-center rounded-full text-ink-mute hover:bg-white">
                          <XIcon className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] font-medium text-peach-700 bg-peach-50 border border-peach-100">
                    <Plus className="w-3 h-3" /> Add another
                  </span>
                </span>
              ) : (
                <span className="block text-sm text-ink-mute font-medium mt-0.5">
                  Add one or more medications
                </span>
              )}
            </span>
            <ChevronDown className="w-4 h-4 text-ink-light shrink-0 mt-3" />
          </div>
          {medOpen && (
            <div className="absolute left-0 right-0 top-full mt-2 rounded-2xl bg-white border border-surface-line shadow-floating overflow-hidden z-30">
              <div className="p-2 border-b border-surface-line">
                <input value={medFilter} onChange={(e) => setMedFilter(e.target.value)}
                  data-testid="routine-medication-search"
                  placeholder="Search medications…"
                  className="w-full h-10 px-3 rounded-xl bg-cream-50 text-sm text-ink placeholder:text-ink-light focus:outline-none border border-surface-line" />
              </div>
              <ul className="max-h-72 overflow-auto py-1">
                {filteredMeds.map((m) => {
                  const checked = medIds.includes(m.id);
                  return (
                    <li key={m.id}>
                      <button type="button" onClick={() => { toggleMed(m.id); setMedFilter(""); }}
                        data-testid={`routine-medication-option-${m.id}`}
                        className={cn("w-full text-left px-4 py-3 hover:bg-cream-100 flex items-center gap-3",
                          checked && "bg-peach-50/50")}>
                        <span className={cn("inline-flex w-5 h-5 items-center justify-center rounded-md border shrink-0",
                          checked ? "bg-ink border-ink text-cream-50" : "bg-white border-surface-line text-transparent")}>
                          <Check className="w-3 h-3" strokeWidth={3} />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-[15px] font-medium text-ink truncate">
                            {m.name || m.generic_name || m.id}
                          </span>
                          <span className="block text-xs text-ink-mute truncate">{m.generic_name}</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        {/* Routine steps — guided one-at-a-time UX */}
        <div className="mt-8" data-testid="routine-steps">
          {/* Progress strip */}
          <div className="flex items-center gap-2 mb-5" aria-label="Routine progress">
            {steps.map((s, idx) => {
              const done = !!s.product || s.text.trim().length > 0;
              const isCurrent = idx === activeStep;
              return (
                <button
                  key={s.slot}
                  type="button"
                  onClick={() => goToStep(idx)}
                  data-testid={`routine-progress-${s.slot}`}
                  className={cn(
                    "flex-1 h-1.5 rounded-full transition-all",
                    isCurrent
                      ? "bg-ink"
                      : done
                        ? "bg-emerald-400"
                        : "bg-cream-200 hover:bg-cream-300",
                  )}
                  aria-label={`Go to step ${idx + 1}: ${s.label}`}
                  aria-current={isCurrent ? "step" : undefined}
                />
              );
            })}
          </div>

          {/* Step pills (clickable label row) */}
          <div className="flex flex-wrap gap-1.5 mb-5">
            {steps.map((s, idx) => {
              const isCurrent = idx === activeStep;
              const done = !!s.product || s.text.trim().length > 0;
              return (
                <button
                  key={s.slot}
                  type="button"
                  onClick={() => goToStep(idx)}
                  data-testid={`routine-step-pill-${s.slot}`}
                  className={cn(
                    "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-medium transition-all",
                    isCurrent
                      ? "bg-ink text-cream-50"
                      : done
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : "bg-white text-ink-mute border border-surface-line hover:border-ink-light",
                  )}
                >
                  {done && !isCurrent && <Check className="w-3 h-3" strokeWidth={3} />}
                  {s.label}
                </button>
              );
            })}
          </div>

          {/* Active step card */}
          {steps[activeStep] && (
            <RoutineStepCard
              key={steps[activeStep].slot}
              slot={steps[activeStep].slot}
              label={steps[activeStep].label}
              hint={steps[activeStep].hint}
              stepNumber={activeStep + 1}
              totalSteps={steps.length}
              isLast={activeStep === steps.length - 1}
              selected={steps[activeStep].product}
              onSelect={(p) => {
                setStep(activeStep, "product", p);
                // Confirmation only — no automatic step advance or scroll.
                // User reported any auto page movement as disruptive, so we
                // hand control back: a toast confirms the add and the user
                // taps "Next" themselves when ready.
                if (activeStep < steps.length - 1) {
                  const { brand: bShow, name: nShow } = splitBrandAndName(p);
                  toast.success(
                    bShow ? `${bShow} · ${nShow} added` : `${nShow} added`,
                    { duration: 1000 },
                  );
                }
              }}
              onClear={() => setStep(activeStep, "product", null)}
              onSkip={advance}
              onNext={advance}
              catalog={catalog}
              optionalIngredientText={steps[activeStep].text}
              onOptionalIngredientChange={(v) => setStep(activeStep, "text", v)}
              onProductNameChange={(v) => setStep(activeStep, "productName", v)}
              photoUsage={photoUsage}
              onScanPhoto={() => openPhotoForStep(activeStep)}
            />
          )}

          {/* Back button (visible from step 2 onwards) */}
          {activeStep > 0 && (
            <button
              type="button"
              onClick={goBack}
              data-testid="routine-step-back"
              className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-ink-mute hover:text-ink"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to {steps[activeStep - 1].label}
            </button>
          )}
        </div>

        {running && (
          <p
            data-testid="routine-running-status"
            className="mt-6 text-sm text-ink-mute italic text-center"
          >
            Checking your routine…
          </p>
        )}

        {/* Results — always renders below the active step. Never blocked by
            auth/pro gates. Quota-exhausted users get an inline "limit reached"
            panel here instead of an overlay modal. */}
        {result && (
          <div className="mt-8 space-y-4" data-testid="routine-result">
            {result.quota_exhausted ? (
              <div
                data-testid="routine-quota-exhausted"
                className="rounded-3xl bg-cream-50 border border-surface-line p-6"
              >
                <p className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-ink-mute">
                  Free monthly limit reached
                </p>
                <h2 className="mt-1 text-2xl font-extrabold text-ink">
                  {result.message}
                </h2>
                <p className="mt-2 text-sm text-ink-mute">
                  Sign in or upgrade to keep checking unlimited routines.
                </p>
                <button
                  type="button"
                  onClick={() => onAuthNeeded?.()}
                  data-testid="routine-quota-cta"
                  className="mt-4 inline-flex items-center h-11 px-5 rounded-2xl bg-ink text-cream-50 text-sm font-bold hover:opacity-90"
                >
                  Sign in to continue
                </button>
              </div>
            ) : (
              <>
                <div
                  data-testid="routine-verdict-banner"
                  className={cn(
                    "rounded-3xl border p-6",
                    VERDICT_TONE[result.routine_verdict] || VERDICT_TONE.INSUFFICIENT_DATA,
                  )}
                >
                  <p className="text-[11px] uppercase tracking-[0.22em] font-bold opacity-80">
                    Routine verdict
                  </p>
                  <div className="flex items-start gap-3 mt-1">
                    <VerdictIcon verdict={result.routine_verdict} className="w-7 h-7 shrink-0 mt-0.5" />
                    <h2 className="text-2xl sm:text-3xl font-extrabold leading-tight">
                      {VERDICT_LABEL[result.routine_verdict] || result.routine_verdict}
                    </h2>
                  </div>
                  {routineCountsSummary(result.verdict_counts) && (
                    <p data-testid="routine-verdict-summary" className="mt-3 text-sm font-semibold opacity-80">
                      {routineCountsSummary(result.verdict_counts)}
                    </p>
                  )}
                  <p className="mt-3 text-[11.5px] leading-relaxed opacity-70">
                    Information from the FDA label and published sources. Not medical advice.
                  </p>
                </div>

                {/* Save routine — gated on auth, hidden when auth is fully
                    disabled so we don't tease a feature that can't ship. */}
                {authEnabled !== false && (
                  <div
                    data-testid="routine-save-block"
                    className="rounded-2xl border border-surface-line bg-cream-100 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      {savedRoutineId ? (
                        <p className="text-sm font-semibold text-ink flex items-center gap-2">
                          <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                          Saved — find it on{" "}
                          <a
                            href="/routines"
                            className="underline underline-offset-4 font-bold"
                          >
                            My routines
                          </a>.
                        </p>
                      ) : (
                        <>
                          <p className="text-[13px] font-bold text-ink">
                            Save this routine for later
                          </p>
                          <input
                            type="text"
                            value={routineLabel}
                            onChange={(e) => setRoutineLabel(e.target.value)}
                            maxLength={80}
                            placeholder="Optional label (e.g. Night routine)"
                            data-testid="routine-save-label-input"
                            className="mt-2 w-full bg-white border border-surface-line rounded-xl h-10 px-3 text-[13px] outline-none focus:border-ink/40 focus:ring-2 focus:ring-ink/10"
                          />
                        </>
                      )}
                    </div>
                    {!savedRoutineId && (
                      <button
                        type="button"
                        onClick={handleSaveRoutine}
                        disabled={savingRoutine}
                        data-testid="routine-save-btn"
                        className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-2xl bg-ink hover:bg-ink-soft text-cream-50 font-bold text-sm transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:cursor-wait shrink-0"
                      >
                        {savingRoutine
                          ? "Saving…"
                          : user ? "Save routine" : "Sign in to save"}
                      </button>
                    )}
                  </div>
                )}

                <div className="grid gap-3">
                  {result.products.map((p, idx) => {
                    const isInsufficient = p.verdict === "INSUFFICIENT_DATA" || p.product_not_found;
                    const findings = p.verified_findings || [];
                    const { brand: rb, name: rn } = splitBrandAndName(p.product || { name: p.label });
                    return (
                    <div key={idx} className={cn(
                      "rounded-2xl bg-white border p-4 sm:p-5",
                      VERDICT_BORDER[p.verdict] || VERDICT_BORDER.INSUFFICIENT_DATA,
                    )}
                    data-testid={`routine-product-result-${p.slot}`}>
                      <div className="flex items-start gap-3">
                        <VerdictIcon
                          verdict={p.verdict}
                          className={cn(
                            "w-5 h-5 shrink-0 mt-0.5",
                            p.verdict === "AVOID" && "text-rose-600",
                            p.verdict === "USE_WITH_CAUTION" && "text-amber-600",
                            p.verdict === "SAFE_TOGETHER" && "text-emerald-600",
                            p.verdict === "INSUFFICIENT_DATA" && "text-ink-mute",
                          )}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-ink-mute">
                            {p.slot}
                          </p>
                          <p className="text-sm font-bold text-ink truncate flex items-baseline gap-1.5">
                            {rb && (
                              <span className="text-[10.5px] uppercase tracking-[0.14em] font-medium text-ink-mute shrink-0">
                                {rb}
                              </span>
                            )}
                            {rb && <span className="text-ink-mute select-none" aria-hidden="true">|</span>}
                            <span className="truncate">{rn}</span>
                          </p>
                          <span className={cn(
                            "inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold border",
                            VERDICT_TONE[p.verdict] || VERDICT_TONE.INSUFFICIENT_DATA,
                          )}>
                            {VERDICT_LABEL[p.verdict] || p.verdict}
                          </span>
                        </div>
                      </div>

                      {/* Per-product evidence — SAME source strip / insufficient
                          panel as the single-product checker (shared component). */}
                      {isInsufficient ? (
                        <InsufficientActivesPanel
                          result={{ ...p, medications: result.medications }}
                          showRoutineLink={false}
                          className="mt-4 bg-cream-50 border border-surface-line"
                        />
                      ) : (
                        <div className="mt-4 space-y-3" data-testid={`routine-findings-${p.slot}`}>
                          {findings.map((f, i) => (
                            <VerifiedFinding key={`${f.ingredient_id}-${f.medication_id}-${i}`} f={f} />
                          ))}
                          <UncoveredActivesNote uncovered={p.uncovered_actives} />
                          {findings.length === 0 && (
                            <p className="text-[13px] text-ink-soft leading-relaxed">
                              No flagged interactions — every active in this product has a verified, sourced note.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>

                {/* Download the routine's Discussion Report (Pro). */}
                <div
                  data-testid="routine-result-actions"
                  className="flex flex-wrap gap-2 pt-2"
                >
                  <button
                    type="button"
                    onClick={handleDownloadReport}
                    disabled={downloadingReport}
                    data-testid="routine-download-report"
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-ink hover:bg-ink-soft text-cream-50 text-[13px] font-semibold transition-colors disabled:opacity-60"
                  >
                    {downloadingReport ? "Preparing…" : "Download Discussion Report"}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <PhotoCapture
        open={photoOpen}
        onOpenChange={(v) => {
          setPhotoOpen(v);
          if (!v) {
            setPhotoPasteOnly(false);
            refreshPhotoUsage();
          }
        }}
        pasteOnly={photoPasteOnly}
        onLimitReached={(info) => {
          setPhotoLimit(info);
          setPhotoUsage((u) => ({
            used:      info?.used  ?? (u?.used ?? 3),
            limit:     info?.limit ?? (u?.limit ?? 3),
            remaining: 0,
          }));
        }}
        onConfirm={({ ingredients }) => {
          // Pipe extracted ingredients into the targeted step's paste field.
          if (photoTargetIdx == null) return;
          const targetIdx = photoTargetIdx;
          const text = (ingredients || []).join(", ");
          setStep(targetIdx, "text", text);
          const stepLabel = steps[targetIdx]?.label || "step";
          toast.success(
            `Loaded ${ingredients.length} ingredients into ${stepLabel}`,
            { duration: 1000 },
          );
          // Confirmation only — no automatic step advance or scroll. User
          // taps "Next" themselves when ready (avoids any unexpected page
          // movement on mobile after a photo scan).
        }}
      />
      <PhotoOcrLimitModal
        open={!!photoLimit}
        onOpenChange={(v) => { if (!v) setPhotoLimit(null); }}
        used={photoLimit?.used ?? 3}
        limit={photoLimit?.limit ?? 3}
        authEnabled={authEnabled}
        onCreateAccount={() => {
          setPhotoLimit(null);
          if (authEnabled) {
            onAuthNeeded?.();
          } else {
            toast.message("Free accounts launch soon", {
              description: "Until then, paste your ingredient list — it's unlimited.",
            });
          }
        }}
        onPasteInstead={() => {
          setPhotoLimit(null);
          setPhotoPasteOnly(true);
          setPhotoOpen(true);
        }}
      />
    </main>
  );
}
